<template>
  <div
    class="fixed inset-0 flex items-center justify-center z-50"
  >
    <div class="fixed inset-0 bg-black opacity-50" />
    <div
      v-if="uploading || finished"
      class="relative bg-white rounded-lg shadow-lg transition-all mx-30 overflow-hidden p-10"
    >
      <div
        v-if="finished"
        class="mb-4"
      >
        {{ modelStore.modelName }}s Added!
      </div>
      <div
        v-else
        class="mb-4"
      >
        Uploading...
      </div>
      <loading-spinner
        :max="finished ? 100 : modelStore.tempRecords.length"
        :value="finished ? 100 : uploadedRecords"
      >
        <div
          v-if="!finished"
          class="text-xs"
        >
          {{ uploadedRecords }}/{{ modelStore.tempRecords.length }}
        </div>
      </loading-spinner>
    </div>
    <div
      v-else
      class="relative bg-white rounded-lg shadow-lg transition-all md:w-4/5 mx-4 md:mx-10 w-screen max-h-[90vh] h-[85vh] m-4 md:m-10"
    >
      <div class="h-full w-full flex flex-col overflow-hidden">
        <!-- HEADER-->
        <div class="header w-full">
          <div class="flex flex-row justify-between items-center p-6">
            <div>
              <p class="text-lg font-semibold">
                {{ modalTitle }}
              </p>
            </div>
            <button
              class="text-gray-500 hover:text-gray-700 focus:outline-none"
              @click="closeModal"
            >
              <svg
                class="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div class="border-t border-gray-300 mx-4" />
        </div>
        <!-- Modal body -->

        <div
          v-if="isScreenLargeEnough"
          class="flex flex-row flex-1 modal-body w-full min-h-0 overflow-hidden"
        >
          <div class="left flex flex-col border-r-gray-400 min-h-0">
            <!-- Tab headers -->
            <div class="flex flex-row w-full">
              <button
                :class="['flex-1 py-2 text-center', { 'bg-default_primary_dark text-white': activeTab === 'tab1', 'bg-white text-gray-500': activeTab !== 'tab1' }]"
                class="transition-colors duration-300"
                @click="switchTab('tab1')"
              >
                Add One {{ modelStore.modelName }}
              </button>
              <button
                :class="['flex-1 py-2 text-center', { 'bg-default_primary_dark text-white': activeTab === 'tab2', 'bg-white text-gray-500': activeTab !== 'tab2' }]"
                class="transition-colors duration-300"
                @click="switchTab('tab2')"
              >
                Bulk Upload
              </button>
            </div>

            <!-- Tab content with sliding animation -->
            <transition
              name="slide"
              @before-enter="beforeEnter"
              @enter="enter"
              @leave="leave"
            >
              <div
                :key="activeTab"
                class="tab-content flex-1 min-h-0 overflow-hidden"
              >
                <div
                  v-show="activeTab === 'tab1'"
                  class="tab-pane"
                >
                  <div class="h-full">
                    <div class="bg-white p-3 h-full">
                      <!-- TAB 1 Content-->
                      <!-- Generic table for importer, pass in the model template from the store -->
                      <excel-import-form
                        :model="modelStore.modelTemplate"
                        :donor-search="modelStore.searchDonors || null"
                        @add-record="modelStore.addToTempList"
                      />
                    </div>
                  </div>
                </div>
                <div
                  v-show="activeTab === 'tab2'"
                  class="tab-pane"
                >
                  <div class="bg-white p-3 h-full">
                    <!-- TAB 2 Content-->
                    <excel-file-uploader
                      :model-store="modelStore"
                    />
                  </div>
                </div>
              </div>
            </transition>
          </div>

          <!-- Preview / Upload -->
          <div class="right bg-white z-10 flex flex-col">
            <div class="flex-1 overflow-y-auto min-h-0">
              <excel-importer-preview
                :model-store="modelStore"
              />
            </div>
            <!--   Add Offline Gift Button  -->
            <div class="shrink-0 p-2">
              <button
                class="w-full px-4 py-2 rounded bg-default_primary_dark hover:bg-default_primary_darkest text-default_white cursor-pointer disabled:bg-gray-400 disabled:cursor-not-allowed"
                :disabled="!uploadButtonEnabled"
                @click="uploadRecords"
              >
                Add {{ modelStore.modelName }}s
              </button>
            </div>
          </div>
        </div>
        <div
          v-else
          class="flex justify-center items-center text-center text-2xl h-full"
        >
          <p class="p-2">
            Please view on a larger screen to use this feature!
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import ExcelImportForm from "@/javascript/views/components/excelImport/ExcelImportForm.vue";
import {useI18n} from "vue-i18n";
import ExcelImporterPreview from "@/javascript/views/components/excelImport/ExcelImporterPreview.vue";
import ExcelFileUploader from "@/javascript/views/components/excelImport/ExcelFileUploader.vue";
import LoadingSpinner from "@/javascript/views/components/loadingSpinner.vue";

export default defineComponent({

  name: 'ExcelImporter',
  components: {
    LoadingSpinner,
    ExcelFileUploader,
    ExcelImporterPreview,
    ExcelImportForm

  },
  props: {
    /**
     * PROP: modelStore
     * Store passed to process the data coming from the importer
     *
     * *The following methods are required in the store to work properly
     * @function downloadTemplate - Downloads the models template excel file
     * @function parseTemplate - Takes the filled out template file returns an array of formatted records
     * @function addToTempList - Takes one record and add it to the list of temporary records that are to be reviewed uploaded.
     *
     * *The following properties are required in the store for the component to work properly
     * @property {Array} tempRecords - Empty array used across the component to store temporary records staged for upload
     * @property {Object} modelTemplate - Template of the model in a specific format. Used for dynamic form builder. See ExcelImportForm.vue for specifics
     * @property {String} modelName - Model getting bulk uploaded for titles and such
     **/
    modelStore: {
      type: Object,
      required: true,
    },
  },
  emits: ['close'],

  setup() {
    // I18n
    const {t} = useI18n();

    return {t};
  },

  data() {
    return {
      activeTab: 'tab1',
      previousTab: null,
      uploading: false,
      finished: false,
      isScreenLargeEnough: window.innerWidth >= 768 && window.innerHeight >= 500,
    };
  },

  computed: {
    uploadButtonEnabled() {
      return this.modelStore.tempRecords.length > 0;
    },
    uploadedRecords() {
      let uploaded = this.modelStore.tempRecords.filter(record => record.uploaded).length;
      return uploaded > 0 ? uploaded : 1;
    },

    // Modal title
    // ie Add a record / Add an offer
    modalTitle() {
      const modelName = this.modelStore.modelName;
      if (!modelName) return "";

      const firstLetter = modelName.trim()[0].toLowerCase();

      const article = ['a', 'e', 'i', 'o', 'u'].includes(firstLetter) ? "Add an" : "Add a";
      return `${article} ${modelName}`;
    }
  },
  mounted() {
    window.addEventListener('resize', this.checkScreenSize);
    this.checkScreenSize();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkScreenSize);
  },
  methods: {
    closeModal() {
      this.$emit('close');
    },

    checkScreenSize() {
      this.isScreenLargeEnough = window.innerWidth >= 768 && window.innerHeight >= 500;
    },

    switchTab(tab){
      this.previousTab = this.activeTab;
      this.activeTab = tab;
    },

    async uploadRecords() {
      this.uploading = true;
      await this.modelStore.addRecords();
      this.finished = true;
      setTimeout(() => {
        // eslint-disable-next-line vue/no-mutating-props
        this.modelStore.tempRecords = [];
        this.closeModal();
      }, 3000);
    },

    // TRANSITION FUNCTIONS
    beforeEnter(el){
      el.style.transform = `translateX(${this.previousTab === 'tab1' && this.activeTab === 'tab2' ? '100%' : '-100%'})`;
    },

    enter(el, done){
      // Trigger reflow
      el.offsetWidth; // eslint-disable-line  @typescript-eslint/no-unused-expressions
      el.style.transition = 'transform 0.3s ease-in-out';
      el.style.transform = 'translateX(0)';
      done();
    },

    leave(el, done){
      el.style.transition = 'transform 0.3s ease-in-out';
      el.style.transform = `translateX(${this.previousTab === 'tab1' && this.activeTab === 'tab2' ? '-100%' : '100%'})`;
      done();
    }
  },
});
</script>

<style lang="scss">
  .right {
    width: 55%;
    @apply border-l-gray-300;
    @apply border;
  }
  .left {
    width: 45%;
  }

  .tab-pane {
    @apply h-full;
  }
</style>
